/**
 * ui/navigation-ui.js
 * Quản lý điều hướng giao diện: Mobile drawer, FAB toggle, Panel tabs switching, Auto-rotate.
 */

import { defaultSfx } from '../../audio/sfx.js';
import { updateViewOffset } from '../scene-setup.js';

export function initNavigationUI(state, handlers = {}) {
  const mobileToggleFab = document.getElementById('btn-mobile-toggle-panel');
  const mobilePanelBackdrop = document.getElementById('mobile-panel-backdrop');
  const mobileCloseBtn = document.getElementById('btn-close-mobile-panel');
  const controlPanelEl = document.querySelector('.control-panel');

  function updateMobileFabVisibility() {
    if (!mobileToggleFab) return;
    if (state.currentMode === 'drama') {
      mobileToggleFab.classList.add('hidden');
    } else {
      if (!controlPanelEl?.classList.contains('mobile-open')) {
        mobileToggleFab.classList.remove('hidden');
      }
    }
  }

  function openMobileDrawer() {
    if (controlPanelEl) {
      controlPanelEl.classList.add('mobile-open');
    }
    if (mobilePanelBackdrop) {
      mobilePanelBackdrop.classList.add('active');
    }
    if (mobileToggleFab) {
      mobileToggleFab.classList.add('hidden');
    }
    updateViewOffset(state);
  }

  function closeMobileDrawer() {
    if (controlPanelEl) {
      controlPanelEl.classList.remove('mobile-open');
    }
    if (mobilePanelBackdrop) {
      mobilePanelBackdrop.classList.remove('active');
    }
    updateMobileFabVisibility();
    updateViewOffset(state);
  }

  if (mobileToggleFab) {
    mobileToggleFab.addEventListener('click', () => {
      openMobileDrawer();
    });
  }
  if (mobileCloseBtn) {
    mobileCloseBtn.addEventListener('click', () => {
      closeMobileDrawer();
    });
  }
  if (mobilePanelBackdrop) {
    mobilePanelBackdrop.addEventListener('click', () => {
      closeMobileDrawer();
    });
  }

  // --- MOBILE HEADER MORE MENU ---
  const headerMoreBtn = document.getElementById('btn-header-more');
  const headerActions = document.getElementById('header-project-actions');
  const headerMenuBackdrop = document.getElementById('header-menu-backdrop');

  function openHeaderMenu() {
    if (headerActions) headerActions.classList.add('mobile-open');
    if (headerMenuBackdrop) headerMenuBackdrop.classList.add('active');
    if (headerMoreBtn) headerMoreBtn.setAttribute('aria-expanded', 'true');
  }

  function closeHeaderMenu() {
    if (headerActions) headerActions.classList.remove('mobile-open');
    if (headerMenuBackdrop) headerMenuBackdrop.classList.remove('active');
    if (headerMoreBtn) headerMoreBtn.setAttribute('aria-expanded', 'false');
  }

  if (headerMoreBtn) {
    headerMoreBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (headerActions?.classList.contains('mobile-open')) {
        closeHeaderMenu();
      } else {
        openHeaderMenu();
      }
    });
  }

  if (headerMenuBackdrop) {
    headerMenuBackdrop.addEventListener('click', () => {
      closeHeaderMenu();
    });
  }

  if (headerActions) {
    headerActions.querySelectorAll('.header-action-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          closeHeaderMenu();
        }
      });
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (controlPanelEl && controlPanelEl.classList.contains('mobile-open')) {
        closeMobileDrawer();
      }
      closeHeaderMenu();
    }
  });

  // --- STUDIO PANELS NAVIGATION ---
  const studioPanels = {
    customizer: document.getElementById('panel-customizer'),
    characters: document.getElementById('panel-characters'),
    animals: document.getElementById('panel-animals'),
    interaction: document.getElementById('panel-interaction'),
    storyboard: document.getElementById('panel-storyboard')
  };

  function switchStudioPanel(panelName) {
    document.querySelectorAll('.panel-nav-btn').forEach((b) => {
      const isActive = b.dataset.panel === panelName;
      b.classList.toggle('active', isActive);
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    Object.entries(studioPanels).forEach(([key, el]) => {
      if (el) el.style.display = key === panelName ? 'flex' : 'none';
    });

    if (panelName === 'characters' && typeof handlers.renderCharacterRoster === 'function') {
      handlers.renderCharacterRoster();
    } else if (panelName === 'storyboard' && typeof handlers.renderStoryboardShots === 'function') {
      handlers.renderStoryboardShots();
    } else if (panelName === 'animals') {
      if (state.currentMode !== 'animals' && typeof handlers.switchMode === 'function') {
        handlers.switchMode('animals');
      }
    } else if (state.currentMode === 'animals' && typeof handlers.switchMode === 'function') {
      handlers.switchMode('studio', { keepPanel: true });
    }
  }

  document.querySelectorAll('.panel-nav-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      switchStudioPanel(btn.dataset.panel);
    });
  });

  // Auto rotate toggle
  const autoRotateToggle = document.getElementById('toggle-autorotate');
  if (autoRotateToggle) {
    autoRotateToggle.addEventListener('change', (e) => {
      const isReduced =
        typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (state.controls) {
        state.controls.autoRotate = isReduced ? false : e.target.checked;
        state.controls.autoRotateSpeed = 2.0;
      }
    });
  }

  // Phản hồi âm thanh cơ học xúc giác (Tactile feedback)
  document.querySelectorAll('.header-back-btn').forEach((btn) => {
    btn.addEventListener('click', () => defaultSfx.playTactileFeedback(640));
  });
  document
    .querySelectorAll(
      '.scene-tab, .panel-nav-btn, .sub-tab-btn, .chip-btn, .btn-action, .mobile-toggle-fab, .mobile-drawer-close'
    )
    .forEach((el) => {
      el.addEventListener('click', () => defaultSfx.playTactileFeedback(480));
    });

  // Quản lý hiển thị gợi ý thao tác chuột (U-9)
  const hintsEl = document.querySelector('.viewport-hints');
  const closeHintsBtn = document.getElementById('btn-close-hints');
  const reopenHintsBtn = document.getElementById('btn-reopen-hints');

  function hideViewportHints(saveStorage = true) {
    if (hintsEl) hintsEl.classList.add('viewport-hints--hidden');
    if (reopenHintsBtn && state.currentMode !== 'drama') {
      reopenHintsBtn.style.display = 'inline-flex';
    }
    if (saveStorage) {
      try {
        localStorage.setItem('vas.hintsDismissed', 'true');
      } catch {}
    }
  }

  function showViewportHints() {
    if (hintsEl) hintsEl.classList.remove('viewport-hints--hidden');
    if (reopenHintsBtn) reopenHintsBtn.style.display = 'none';
    try {
      localStorage.removeItem('vas.hintsDismissed');
    } catch {}
  }

  let hintsDismissed = false;
  try {
    hintsDismissed = localStorage.getItem('vas.hintsDismissed') === 'true';
  } catch {}

  if (hintsDismissed) {
    hideViewportHints(false);
  } else {
    showViewportHints();
  }

  if (closeHintsBtn) {
    closeHintsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      hideViewportHints(true);
    });
  }

  if (reopenHintsBtn) {
    reopenHintsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      showViewportHints();
    });
  }

  let pointerDownCount = 0;
  const canvasContainer = document.getElementById('canvas-container');
  if (canvasContainer) {
    const handleCanvasPointerDown = () => {
      if (hintsEl && hintsEl.classList.contains('viewport-hints--hidden')) return;
      pointerDownCount++;
      if (pointerDownCount >= 3) {
        hideViewportHints(true);
        canvasContainer.removeEventListener('pointerdown', handleCanvasPointerDown);
      }
    };
    canvasContainer.addEventListener('pointerdown', handleCanvasPointerDown);
  }

  return {
    updateMobileFabVisibility,
    openMobileDrawer,
    closeMobileDrawer,
    switchStudioPanel,
    hideViewportHints,
    showViewportHints,
    openHeaderMenu,
    closeHeaderMenu
  };
}

export function scrollActiveSceneTabIntoView() {
  const activeTab = document.querySelector('.scene-tabs .scene-tab.active');
  if (activeTab && typeof activeTab.scrollIntoView === 'function') {
    activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}
